import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Search,
  Download,
  Eye,
  Trash2,
  FileText,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import { moderatorAPI, jobAPI, cvAPI } from "../services/api";

const ModeratorDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [cvDocuments, setCvDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState("job-listings");
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [jobToEdit, setJobToEdit] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [filteredCvDocuments, setFilteredCvDocuments] = useState([]);
  const [deleteType, setDeleteType] = useState("job");
  const [CVToDelete, setCVToDelete] = useState(null);
  const [cvActionLoading, setCvActionLoading] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isAuthenticated = await moderatorAPI.checkAuth();
        if (!isAuthenticated) {
          navigate("/moderator-login");
          return;
        }
        loadDashboardData();
      } catch (error) {
        navigate("/moderator-login");
      }
    };
    checkAuth();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const jobsData = await jobAPI.getAllJobs();
      const cvData = await cvAPI.getCVDocuments();
      // setJobs(jobsData.data || []);
      setCvDocuments(cvData.data || []);
      loadJobs();
      // Simulate the error message from the screenshot
      if (!jobsData.data || jobsData.data.length === 0) {
        // setError("No jobs");
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      // setError("No jobs");
    } finally {
      setLoading(false);
    }
  };

  // Helper to get jobs from localStorage
  function getLocalStorageJobs() {
    try {
      const stored = localStorage.getItem("techmind_jobs");
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.error("Error retrieving jobs from storage:", err);
      return [];
    }
  }

  // Flags for countries
  const flags = {
    "United States":
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='60' height='3.08' y='0' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='6.15' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='12.31' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='18.46' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='24.62' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='30.77' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='36.92' fill='%23d80027'/%3E%3Crect width='24' height='20.77' fill='%232e4b80'/%3E%3C/svg%3E",
    "United Kingdom":
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23012169'/%3E%3Cpath d='M0,0 L60,40 M60,0 L0,40' stroke='%23fff' stroke-width='6'/%3E%3Cpath d='M30,0 L30,40 M0,20 L60,20' stroke='%23fff' stroke-width='10'/%3E%3Cpath d='M30,0 L30,40 M0,20 L60,20' stroke='%23c8102e' stroke-width='6'/%3E%3Cpath d='M0,0 L60,40 M60,0 L0,40' stroke='%23c8102e' stroke-width='2'/%3E%3C/svg%3E",
    India:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='60' height='13.33' y='0' fill='%23ff9933'/%3E%3Crect width='60' height='13.33' y='26.67' fill='%23138808'/%3E%3Ccircle cx='30' cy='20' r='4' fill='%23000080'/%3E%3C/svg%3E",
    "United Arab Emirates":
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='20' height='40' fill='%23009e49'/%3E%3Crect width='60' height='13.33' y='0' fill='%23ce1126'/%3E%3Crect width='60' height='13.33' y='26.67' fill='%23000'/%3E%3C/svg%3E",
    Singapore:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='60' height='20' fill='%23ed2939'/%3E%3Ccircle cx='16' cy='20' r='6' fill='%23f0f0f0'/%3E%3Ccircle cx='20' cy='20' r='6' fill='%23f0f0f0'/%3E%3Cpath d='M11,16 L25,24 M25,16 L11,24' stroke='%23ed2939' stroke-width='1'/%3E%3C/svg%3E",
  };

  // Default jobs
  const defaultJobs = [
    {
      id: 1,
      title: "Senior Software Engineer",
      company: "TechGlobal Inc.",
      location: "United States",
      flag: flags["United States"],
      type: "Full-time",
      industry: "Technology",
      skills: ["React", "Node.js", "AWS"],
      posted: "2 days ago",
      salary: "$120,000 - $150,000",
      experienceLevel: "Senior",
      description:
        "We are seeking a talented Senior Software Engineer to join our team at TechGlobal Inc. This is an exciting opportunity to work on cutting-edge projects in a collaborative environment.",
      responsibilities: [
        "Design, develop, and maintain high-quality software solutions",
        "Lead technical design discussions and architecture decisions",
        "Mentor junior developers and conduct code reviews",
        "Collaborate with cross-functional teams to define and implement new features",
        "Troubleshoot and resolve complex technical issues",
      ],
      requirements: [
        "5+ years of experience in software development",
        "Strong proficiency in React, Node.js, and AWS",
        "Experience with microservices architecture",
        "Excellent problem-solving skills and attention to detail",
        "Bachelor's degree in Computer Science or related field",
      ],
      benefits: [
        "Competitive salary and comprehensive benefits package",
        "Flexible working arrangements and remote options",
        "Professional development budget",
        "Regular team events and activities",
        "Modern office with amenities",
      ],
    },
    {
      id: 2,
      title: "UX/UI Designer",
      company: "Creative Solutions",
      location: "United Kingdom",
      flag: flags["United Kingdom"],
      type: "Full-time",
      industry: "Design",
      skills: ["Figma", "Adobe XD", "Sketch"],
      posted: "3 days ago",
      salary: "£65,000 - £80,000",
      experienceLevel: "Mid-Level",
      description:
        "Creative Solutions is looking for a talented UX/UI Designer to create exceptional user experiences for our clients' digital products. You'll work closely with our product and development teams to design intuitive and visually appealing interfaces.",
      responsibilities: [
        "Create wireframes, prototypes, and high-fidelity designs",
        "Conduct user research and usability testing",
        "Develop user flows and information architecture",
        "Collaborate with developers to ensure design implementation",
        "Stay current with UX/UI trends and best practices",
      ],
      requirements: [
        "3+ years of experience in UX/UI design",
        "Proficiency in Figma, Adobe XD, and Sketch",
        "Strong portfolio demonstrating user-centered design process",
        "Understanding of accessibility standards",
        "Excellent communication and presentation skills",
      ],
      benefits: [
        "Competitive salary with annual bonus",
        "Flexible working hours and remote options",
        "Professional development opportunities",
        "Creative and collaborative work environment",
        "Regular team social events",
      ],
    },
    {
      id: 3,
      title: "Data Scientist",
      company: "Analytics Pro",
      location: "India",
      flag: flags.India,
      type: "Full-time",
      industry: "Data",
      skills: ["Python", "TensorFlow", "SQL"],
      posted: "1 week ago",
      salary: "₹1,500,000 - ₹2,000,000",
      experienceLevel: "Mid-Level",
    },
    {
      id: 4,
      title: "DevOps Engineer",
      company: "Cloud Systems",
      location: "United Arab Emirates",
      flag: flags["United Arab Emirates"],
      type: "Full-time",
      industry: "Infrastructure",
      skills: ["Kubernetes", "Docker", "CI/CD"],
      posted: "5 days ago",
      salary: "AED 25,000 - AED 35,000 monthly",
      experienceLevel: "Senior",
    },
    {
      id: 5,
      title: "Full Stack Developer",
      company: "Web Innovators",
      location: "Singapore",
      flag: flags.Singapore,
      type: "Full-time",
      industry: "Technology",
      skills: ["JavaScript", "React", "Node.js", "MongoDB"],
      posted: "Just now",
      salary: "SGD 8,000 - SGD 12,000 monthly",
      experienceLevel: "Mid-Level",
    },
    {
      id: 6,
      title: "Product Manager",
      company: "Digital Solutions",
      location: "United States",
      flag: flags["United States"],
      type: "Full-time",
      industry: "Product",
      skills: ["Agile", "Scrum", "User Research"],
      posted: "3 days ago",
      salary: "$110,000 - $140,000",
      experienceLevel: "Senior",
    },
    {
      id: 7,
      title: "Frontend Developer",
      company: "MobileTech",
      location: "India",
      flag: flags.India,
      type: "Full-time",
      industry: "Technology",
      skills: ["React Native", "JavaScript", "CSS"],
      posted: "2 weeks ago",
      salary: "₹1,200,000 - ₹1,800,000",
      experienceLevel: "Mid-Level",
    },
  ];

  useEffect(() => {
    loadJobs();
  }, []);

  function mergeJobs(localJobs) {
    let data = localStorage.getItem("default_jobs")
      ? JSON.parse(localStorage.getItem("default_jobs"))
      : [];
    const defaultStored = data?.length ? data : defaultJobs;
    // Merge jobs, local first, then default jobs not already in local
    // const localIds = new Set(localJobs.map(j => j.id));
    const merged = [...localJobs, ...defaultStored];
    // for (const job of defaultStored) {
    //   if (!localIds.has(job.id)) {
    //     merged.push(job);
    //   }
    // }
    return merged;
  }

  async function loadJobs() {
    setLoading(true);
    try {
      const localJobs = getLocalStorageJobs();
      // You can also fetch from API if needed, or just use local+default
      const jobsToShow = mergeJobs(localJobs, defaultJobs);
      setJobs(jobsToShow);
      // // For filters
      // setLocation([
      //   "All Locations",
      //   ...Array.from(new Set(jobsToShow.map(job => job.location).filter(Boolean)))
      // ]);
      // setJobsType([
      //   "All Types",
      //   ...Array.from(new Set(jobsToShow.map(job => job.type).filter(Boolean)))
      // ]);
      // setFilteredJobs(jobsToShow);
      // setBackJobs(jobsToShow);
    } catch (err) {
      console.error("Error loading jobs:", err);
    } finally {
      setLoading(false);
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("moderator_token");
    navigate("/moderator-login");
  };

  const handleEditJob = (jobId) => {
    const job = jobs.find((j) => j.id === jobId);
    setJobToEdit(job);
    setEditForm({
      title: job.title || "",
      company: job.company || "",
      companyName: job.companyName || job.company || "",
      location: job.location || "",
      type: job.type || "",
      industry: job.industry || "Technology",
      experienceLevel: job.experienceLevel || job.experience || "",
      salary: job.salary || "",
      description: job.description || "",
      responsibilities: job.responsibilities
        ? Array.isArray(job.responsibilities)
          ? job.responsibilities.join("\n")
          : job.responsibilities
        : "",
      requirements: job.requirements
        ? Array.isArray(job.requirements)
          ? job.requirements.join("\n")
          : job.requirements
        : "",
      benefits: job.benefits
        ? Array.isArray(job.benefits)
          ? job.benefits.join("\n")
          : job.benefits
        : "",
      skills: job.skills
        ? Array.isArray(job.skills)
          ? job.skills.join(", ")
          : job.skills
        : "",
      contactName: job.contactName || "",
      email: job.email || job.contactEmail || "",
      phone: job.phone || job.contactPhone || "",
      deadline: job.deadline || "",
    });
    setShowEditModal(true);
  };

  const handleDeleteJob = (jobId) => {
    setJobToDelete(jobId);
    setDeleteType("job");
    setShowDeleteModal(true);
  };

  const handleDeleteCvDocuments = (jobId) => {
    setCVToDelete(jobId);
    setDeleteType("cv");
    setShowDeleteModal(true);
  };

  const handleViewCv = async (cvId) => {
    try {
      setCvActionLoading({ id: cvId, type: "download" });

      let token = localStorage.getItem("moderator_token");
      const response = await fetch(
        `https://techmindinfosys.com/server/api/download.php?id=${cvId.id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`, // replace with actual token
          },
        }
      );

      if (!response.ok) throw new Error("File not found");

      const blob = await response.blob();

      console.log(blob);

      // Ensure it's a PDF
      const fileType = blob.type;
      if (fileType !== "application/pdf") {
        // throw new Error("Not a valid PDF");
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // a.download = 'cv';
        a.click();
        URL.revokeObjectURL(url);
      }

      const url = window.URL.createObjectURL(blob);

      // Open PDF in a new tab
      window.open(url, "_blank");
      setCvActionLoading({ id: null, type: null });

      // Clean up after 10s
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert("Unable to view document.");
      setCvActionLoading({ id: null, type: null });
    }
  };

  const handleApproveCv = async (cvId) => {
    setCvActionLoading({ id: cvId, type: "approve" });
    await cvAPI.updateCVById(cvId, {
      id: cvId,
      status: "approved",
      notes: "Approved by moderator",
    });
    setCvActionLoading({ id: null, type: null });
    loadDashboardData();
  };

  const handleRejectCv = async (cvId) => {
    setCvActionLoading({ id: cvId, type: "reject" });
    await cvAPI.updateCVById(cvId, {
      id: cvId,
      status: "rejected",
      notes: "Rejected by moderator",
    });
    setCvActionLoading({ id: null, type: null });
    loadDashboardData();
  };

  // const filteredCvDocuments = cvDocuments.filter((cv) => {
  //   const matchesSearch =
  //     cv.applicant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     cv.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     cv.jobTitle.toLowerCase().includes(searchTerm.toLowerCase());
  //   const matchesStatus = statusFilter === "all" || cv.status === statusFilter;
  //   return matchesSearch && matchesStatus;
  // });

  useEffect(() => {
    if (searchTerm) {
      setFilteredCvDocuments(
        cvDocuments.filter((cv) => {
          const matchesSearch =
            cv.applicant_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            cv.email.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesStatus =
            statusFilter === "all" || cv.status === statusFilter;
          return matchesSearch && matchesStatus;
        })
      );
    } else {
      setFilteredCvDocuments(cvDocuments);
    }
  }, [searchTerm, cvDocuments]);

  // Helper for status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-900/50 text-green-300";
      case "pending":
        return "bg-yellow-900/50 text-yellow-300";
      case "rejected":
        return "bg-red-900/50 text-red-300";
      default:
        return "bg-gray-900/50 text-gray-300";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-[#1A193B] pt-20 overflow-hidden">
        <div className="absolute inset-0">
          {/* Diagonal pattern background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-slate-800">
            <div className="absolute inset-0 opacity-10">
              <svg
                className="w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <defs>
                  <pattern
                    id="diagonal-lines"
                    patternUnits="userSpaceOnUse"
                    width="20"
                    height="20"
                    patternTransform="rotate(45)"
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="20"
                      stroke="white"
                      strokeWidth="1"
                      opacity="0.3"
                    />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#diagonal-lines)" />
              </svg>
            </div>
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-800 mb-4">
              Moderator Dashboard
            </h1>
            <p className="text-xl text-slate-600 font-medium">
              Manage job postings and site content_
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">
              TechMind Moderation Platform
            </h1>
            <button
              onClick={handleLogout}
              className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-1">
            <button
              onClick={() => setActiveTab("job-listings")}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${
                activeTab === "job-listings"
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              Job Listings
            </button>
            <button
              onClick={() => setActiveTab("cv-documents")}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${
                activeTab === "cv-documents"
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              CV Documents
            </button>
          </nav>
        </div>

        {/* Error Message */}
        {/* {error && activeTab === "job-listings" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-100 border border-red-300 rounded-md flex items-center"
          >
            <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
            <span className="text-red-800 font-medium">{error}</span>
          </motion.div>
        )} */}

        {/* Content */}
        {activeTab === "job-listings" && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Job Listings Management
            </h2>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Job Title
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Company
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Location
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Posted
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {jobs.length > 0 ? (
                      jobs.map((job, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {job.title || "N/A"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {job.company || "N/A"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {job.type || "N/A"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {job.location || "N/A"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {job.created_at
                              ? new Date(job.created_at).toLocaleString()
                              : "1970-01-01 00:00:00"}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex space-x-3">
                              <button
                                onClick={() => handleEditJob(job.id)}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteJob(job.id)}
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colspan="6"
                          class="px-6 py-4 text-center text-gray-500"
                        >
                          No job listings found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "cv-documents" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                CV Documents Management
              </h2>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search CVs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* CV Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <FileText className="w-8 h-8 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Total CVs</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {filteredCvDocuments.length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <Clock className="w-8 h-8 text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Pending</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {
                        filteredCvDocuments.filter(
                          (cv) => cv.status === "pending"
                        ).length
                      }
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Approved</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {
                        filteredCvDocuments.filter(
                          (cv) => cv.status === "approved"
                        ).length
                      }
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <XCircle className="w-8 h-8 text-red-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Rejected</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {
                        filteredCvDocuments.filter(
                          (cv) => cv.status === "rejected"
                        ).length
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CV Documents Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        ID
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Applicant
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Email
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        File
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Submitted
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredCvDocuments.length ? (
                      filteredCvDocuments.map((cv) => (
                        <tr key={cv.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {cv.id}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {cv.applicant_name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {cv.email}
                          </td>
                          <td
                            className="px-6 py-4 text-sm text-blue-700 underline cursor-pointer"
                            onClick={() => handleViewCv(cv)}
                            title="View CV"
                          >
                            {cv.file_name}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(
                                cv.status
                              )}`}
                            >
                              {cv.status.charAt(0).toUpperCase() +
                                cv.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {cv.submitted_at}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewCv(cv)}
                                className="text-blue-600 hover:text-blue-800"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {cv.status === "pending" && (
                                <>
                                  <button
                                    onClick={() => handleApproveCv(cv.id)}
                                    disabled={
                                      cvActionLoading.id === cv.id &&
                                      cvActionLoading.type === "approve"
                                    }
                                    className="text-green-600 hover:text-green-800"
                                    title="Approve"
                                  >
                                    {cvActionLoading.id === cv.id &&
                                    cvActionLoading.type === "approve" ? (
                                      <span className="animate-spin w-4 h-4 border-b-2 border-green-600 rounded-full inline-block"></span>
                                    ) : (
                                      <CheckCircle className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleRejectCv(cv.id)}
                                    disabled={
                                      cvActionLoading.id === cv.id &&
                                      cvActionLoading.type === "reject"
                                    }
                                    className="text-yellow-600 hover:text-yellow-800"
                                    title="Reject"
                                  >
                                    {cvActionLoading.id === cv.id &&
                                    cvActionLoading.type === "reject" ? (
                                      <span className="animate-spin w-4 h-4 border-b-2 border-yellow-600 rounded-full inline-block"></span>
                                    ) : (
                                      <AlertCircle className="w-4 h-4" />
                                    )}
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleDeleteCvDocuments(cv.id)}
                                disabled={
                                  cvActionLoading.id === cv.id &&
                                  cvActionLoading.type === "delete"
                                }
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                {cvActionLoading.id === cv.id &&
                                cvActionLoading.type === "delete" ? (
                                  <span className="animate-spin w-4 h-4 border-b-2 border-red-600 rounded-full inline-block"></span>
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          No cv documents found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && deleteType && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded shadow-lg">
            <h2 className="text-lg font-bold mb-4">Confirm Delete</h2>
            <p>
              Are you sure you want to delete this{" "}
              {`${deleteType === "job" ? "job" : "cv document"}`}?
            </p>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (deleteType === "job") {
                    // await jobAPI.deleteJob(jobToDelete);
                    let data = JSON.parse(
                      localStorage.getItem("techmind_jobs")
                    );
                    let data1 = localStorage.getItem("default_jobs")
                      ? JSON.parse(localStorage.getItem("default_jobs"))
                      : [];
                    const defaultStored = data1?.length ? data1 : defaultJobs;

                    let updatedData = data?.filter(
                      (item) => item.id !== jobToDelete
                    );
                    let updatedData1 = defaultStored?.filter(
                      (item) => item.id !== jobToDelete
                    );
                    localStorage.setItem(
                      "techmind_jobs",
                      JSON.stringify(updatedData?.length ? updatedData :[])
                    );
                    localStorage.setItem(
                      "default_jobs",
                      JSON.stringify(updatedData1?.length ? updatedData1:[])
                    );
                  } else {
                    await cvAPI.deleteCV(CVToDelete);
                  }
                  setShowDeleteModal(false);
                  loadDashboardData();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && jobToEdit && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Edit Job Listing
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  // Map form data to API payload matching PHP requirements
                  const payload = {
                    id: jobToEdit.id,
                    title: editForm.title,
                    company: editForm.company,
                    companyName: editForm.companyName,
                    location: editForm.location,
                    type: editForm.type,
                    industry: editForm.industry,
                    experienceLevel: editForm.experienceLevel,
                    salary: editForm.salary,
                    description: editForm.description,
                    responsibilities: editForm.responsibilities
                      ? editForm.responsibilities
                          .split("\n")
                          .filter((line) => line.trim())
                      : [],
                    requirements: editForm.requirements
                      ? editForm.requirements
                          .split("\n")
                          .filter((line) => line.trim())
                      : [],
                    benefits: editForm.benefits
                      ? editForm.benefits
                          .split("\n")
                          .filter((line) => line.trim())
                      : [],
                    skills: editForm.skills
                      ? typeof editForm.skills === "string"
                        ? editForm.skills
                            .split(",")
                            .map((skill) => skill.trim())
                            .filter((skill) => skill)
                        : Array.isArray(editForm.skills)
                        ? editForm.skills
                        : []
                      : [],
                    contactName: editForm.contactName,
                    email: editForm.email,
                    phone: editForm.phone,
                    deadline: editForm.deadline || null,
                  };
                  try {
                    // await jobAPI.updateJob(jobToEdit.id, payload);
                    let data = JSON.parse(
                      localStorage.getItem("techmind_jobs")
                    );
                    let data1 = localStorage.getItem("default_jobs")
                      ? JSON.parse(localStorage.getItem("default_jobs"))
                      : [];
                    const defaultStored = data1?.length ? data1 : defaultJobs;

                    let updatedData = (data||[])?.map((item) =>
                      item.id == jobToEdit.id ? payload : item
                    );
                    localStorage.setItem(
                      "techmind_jobs",
                      JSON.stringify(updatedData)
                    );
                    let updatedData1 = (defaultStored||[])?.map(
                      (item) => item.id == jobToEdit.id ? payload : item
                    );
                    localStorage.setItem(
                      "default_jobs",
                      JSON.stringify(updatedData1)
                    );
                    setShowEditModal(false);
                    setTimeout(() => {
                      loadDashboardData();
                    }, 500);
                  } catch (error) {
                    setShowEditModal(false);
                    loadDashboardData();
                  }
                }}
                className="space-y-4"
              >
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Job Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, title: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Company</label>
                    <input
                      type="text"
                      value={editForm.company}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, company: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Display Company Name
                    </label>
                    <input
                      type="text"
                      value={editForm.companyName}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          companyName: e.target.value,
                        }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      value={editForm.contactName}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          contactName: e.target.value,
                        }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Location</label>
                    <input
                      type="text"
                      value={editForm.location}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, location: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Salary</label>
                    <input
                      type="text"
                      value={editForm.salary}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, salary: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Type</label>
                    <select
                      value={editForm.type}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, type: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="full-time">Full Time</option>
                      <option value="part-time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="temporary">Temporary</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Industry</label>
                    <select
                      value={editForm.industry}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, industry: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="Technology">Technology</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Finance">Finance</option>
                      <option value="Education">Education</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Retail">Retail</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Experience Level
                    </label>
                    <select
                      value={editForm.experienceLevel}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          experienceLevel: e.target.value,
                        }))
                      }
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="entry">Entry Level</option>
                      <option value="junior">Junior (1-3 years)</option>
                      <option value="mid">Mid Level (3-5 years)</option>
                      <option value="senior">Senior (5+ years)</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Deadline</label>
                    <input
                      type="date"
                      value={editForm.deadline}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, deadline: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-medium mb-1">
                    Skills (comma separated)
                  </label>
                  <input
                    type="text"
                    value={editForm.skills}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, skills: e.target.value }))
                    }
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        description: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2 min-h-[100px]"
                  />
                </div>
                {/* <div>
                  <label className="block font-medium mb-1">
                    Responsibilities (one per line)
                  </label>
                  <textarea
                    value={editForm.responsibilities}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        responsibilities: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2 min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">
                    Requirements (one per line)
                  </label>
                  <textarea
                    value={editForm.requirements}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        requirements: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2 min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">
                    Benefits (one per line)
                  </label>
                  <textarea
                    value={editForm.benefits}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, benefits: e.target.value }))
                    }
                    className="w-full border rounded px-3 py-2 min-h-[80px]"
                  />
                </div> */}
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, email: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, phone: e.target.value }))
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {cvActionLoading.id && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default ModeratorDashboardPage;
